import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, withModifiers as _withModifiers, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5546744a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tir-pm-datepicker__header" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  key: 1,
  class: "tir-pm-datepicker__header-right"
}
const _hoisted_5 = {
  key: 2,
  class: "tir-pm-datepicker__header-label"
}

import { computed, nextTick, onMounted, onUnmounted, PropType, provide, ref, watch } from "vue";
import { DatepickerTypeEnum, PickerGridTypeEnum } from "./enums";
import {
  DatepickerInput,
  DatesGrid,
  HoursGrid,
  MinutesGrid,
  MonthsGrid,
  NavigationConrols,
  PickerContainer,
  YearsGrid,
} from "./components";
import { TirPmButton, TirPmButtonSizeEnum, TirPmButtonVariantEnum } from "tir-pm-button";
import { ChevronDownIcon, ClockIcon, CalendarIcon } from "tir-style-system/icons/outline";
import { locale } from "./constants";
import { PopoverPositionEnum } from "tir-pm-popover";
import { uuidv4 } from "./utils";
import type { DatepickerInputProps, DatepickerPopoverProps } from "./types";


export default /*#__PURE__*/_defineComponent({
  __name: 'tir-pm-datepicker',
  props: {
  /**Выбранная дата */
  modelValue: { type: Date },
  /**Тип пикера */
  type: {
    type: String as PropType<DatepickerTypeEnum>,
    default: () => DatepickerTypeEnum.Date,
  },
  /**Заблокированно */
  isDisabled: { type: Boolean, default: false },
  /**Закрыть после выбора */
  closeAftetSelection: { type: Boolean, default: true },
  /**Закрывать ли другие открытые пикеры при открытии текущего */
  isCloseOtherPickers: { type: Boolean, default: true },
  /**Пропустить пикер при событии закрытия всех */
  skipCloseOtherPickers: { type: Boolean, default: false },
  /**Интервал минут */
  minutesInterval: { type: Number, default: 15 },
  /**Настройки инпута */
  input: { type: Object as PropType<DatepickerInputProps> },
  /**Настройки поповера */
  popover: { type: Object as PropType<DatepickerPopoverProps> },
},
  emits: ["update:modelValue", "enter", "blur", "save"],
  setup(__props, { expose: __expose, emit }) {

const props = __props;





/**Идентификатор */
const uuid = ref("");
provide("datepicker-uuid", uuid);
/**Ссылка на инпут */
const $datepickerInput = ref<InstanceType<typeof DatepickerInput>>();

/**Контейнер пикера */
const $pickerContainer = ref<InstanceType<typeof PickerContainer>>();
/**Закрыть пикер */
const close = () => $pickerContainer.value?.close();
/**Открыть пикер */
const open = () => $pickerContainer.value?.open();
/**Открыть/закрыть пикер */
const toggle = () => $pickerContainer.value?.toggle();
/**Закрыть все остальные пикеры */
const closeOtherPickers = () => {
  if (props.isCloseOtherPickers) {
    document.dispatchEvent(
      new CustomEvent("closeOtherPickers", {
        detail: { ignore: [uuid.value] },
      }),
    );
  }
};
/**При событии закрытия всех пикеров */
const onCloseOtherPickers = (event: any) => {
  // Пропустить текущий
  const isSkip = props.skipCloseOtherPickers || event.detail?.ignore?.includes(uuid.value);
  if (!isSkip) close();
};

/**Пропсы поповера */
const popoverProps = computed(() => {
  const defaultProps: DatepickerPopoverProps = {
    position: PopoverPositionEnum.Bottom,
    isNoTeleport: false,
  };

  return props.popover ? Object.assign(defaultProps, props.popover) : defaultProps;
});
/**Пропсы инпута */
const inputProps = computed(() => {
  const defaultProps: DatepickerInputProps = {
    isSmall: false,
    isShowHint: false,
    isWithHint: false,
    isWithError: false,
    isWithSuccess: false,
  };
  return props.input ? Object.assign(defaultProps, props.input) : defaultProps;
});

/**
 * * Получчить тип сетки на основании типа датапикера
 * @param type
 */
const getGridTypeByDatepickerType = (type: DatepickerTypeEnum) => {
  return {
    [DatepickerTypeEnum.Date]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.DateTime]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.Week]: PickerGridTypeEnum.Dates,
    [DatepickerTypeEnum.Month]: PickerGridTypeEnum.Months,
    [DatepickerTypeEnum.Year]: PickerGridTypeEnum.Years,
    [DatepickerTypeEnum.Time]: PickerGridTypeEnum.Hours,
  }[type];
};
/**Отображаемый тип пикера */
const viewGridType = ref(getGridTypeByDatepickerType(props.type));
/**Получить отображаемый год */
const getViewYear = () => (props.modelValue ?? new Date()).getFullYear();
/**Получить отображаемый месяц */
const getViewMonth = () => (props.modelValue ?? new Date()).getMonth();
/**Отображаемый год */
const viewYear = ref<number>(getViewYear());
/**Отображаемый месяц */
const viewMonth = ref<number>(getViewMonth());
/**Открытие по фокусу */
const openFromFocus = computed(() =>
  [DatepickerTypeEnum.Month, DatepickerTypeEnum.Year, DatepickerTypeEnum.Week].includes(props.type),
);
/**Значение для DatesGrid */
const datesGridValue = computed({
  get() {
    return props.modelValue;
  },
  async set(value) {
    emit("update:modelValue", value);
    emit("save");
    if (props.closeAftetSelection && props.type !== DatepickerTypeEnum.DateTime) {
      await nextTick();
      close();
    }
  },
});
/**Значение для MonthsGrid */
const monthsGridValue = computed({
  get() {
    switch (props.type) {
      case DatepickerTypeEnum.Month:
        return props.modelValue?.getMonth();
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        return viewMonth.value;
    }
  },
  async set(value) {
    if (value === undefined) return;

    switch (props.type) {
      case DatepickerTypeEnum.Month: {
        const date = new Date(props.modelValue ?? new Date());
        date.setMonth(value);
        emit("update:modelValue", date);
        emit("save");
        await nextTick();
        if (props.closeAftetSelection) close();
        break;
      }
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        viewMonth.value = value;
        viewGridType.value = PickerGridTypeEnum.Dates;
    }
  },
});
/**Значение для YearsGrid */
const yearsGridValue = computed({
  get() {
    switch (props.type) {
      case DatepickerTypeEnum.Year:
        return props.modelValue?.getFullYear();
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.Month:
      case DatepickerTypeEnum.DateTime:
        return viewYear.value;
    }
  },
  async set(value) {
    if (value === undefined) return;

    switch (props.type) {
      case DatepickerTypeEnum.Year: {
        const date = new Date(props.modelValue ?? new Date());
        date.setFullYear(value);
        emit("update:modelValue", date);
        emit("save");
        await nextTick();
        if (props.closeAftetSelection) close();
        break;
      }
      case DatepickerTypeEnum.Month:
      case DatepickerTypeEnum.Date:
      case DatepickerTypeEnum.Week:
      case DatepickerTypeEnum.DateTime:
        viewYear.value = value;
        viewGridType.value = PickerGridTypeEnum.Months;
    }
  },
});
/**Значение для HoursGrid */
const hoursGridValue = computed({
  get() {
    return props.modelValue?.getHours();
  },
  set(value) {
    if (value === undefined) return;
    const date = new Date(props.modelValue ?? new Date());
    date.setHours(value);
    emit("update:modelValue", date);
    emit("save");
    viewGridType.value = PickerGridTypeEnum.Minutes;
  },
});
/**Значение для MinutesGrid */
const minutesGridValue = computed({
  get() {
    return props.modelValue?.getMinutes();
  },
  async set(value) {
    if (value === undefined) return;
    const date = new Date(props.modelValue ?? new Date());
    date.setMinutes(value);
    emit("update:modelValue", date);
    emit("save");
    await nextTick();
    if (props.closeAftetSelection) close();
    else viewGridType.value = getGridTypeByDatepickerType(props.type);
  },
});
/**Получить первый год в списке */
const getFirstYear = () => {
  const startYear = 2000;
  const toViewYear = viewYear.value;
  const countElementInList = 12;

  // Вычисляем смещение, округляя вверх или вниз, чтобы получить кратный countElementInList
  const offset = Math.floor((toViewYear - startYear) / countElementInList) * countElementInList;

  return startYear + offset;
};

/**Первый отображаемый год */
const firstYear = ref<number>(getFirstYear());

/**Текст кнопки в шапке*/
const headerButtonText = computed(() => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Dates:
      return `${locale.monthNames[viewMonth.value]} ${viewYear.value}`;
    case PickerGridTypeEnum.Months:
      return `${viewYear.value}`;
    case PickerGridTypeEnum.Years:
      return `<span class='years-range-text'>${firstYear.value} - ${firstYear.value + 11}</span>`;
    case PickerGridTypeEnum.Hours:
    case PickerGridTypeEnum.Minutes:
      const date = new Date(props.modelValue ?? new Date());
      return props.type !== DatepickerTypeEnum.Time
        ? `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}.${date.getFullYear()}`
        : undefined;
  }
});
/**Видимость кнопки с часами */
const visibleClockButton = computed(
  () =>
    props.type === DatepickerTypeEnum.DateTime && viewGridType.value === PickerGridTypeEnum.Dates,
);
/**Видимость навигации */
const visibleNavigate = computed(() =>
  [PickerGridTypeEnum.Dates, PickerGridTypeEnum.Years].includes(viewGridType.value),
);
/**Лейбл в хедере */
const headerLabel = computed(() => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Hours:
      return "часы";
    case PickerGridTypeEnum.Minutes:
      return "минуты";
  }
});

/**Клик на кнопку с часами */
const onClickClock = () => {
  viewGridType.value = PickerGridTypeEnum.Hours;
};

/**При клике на кнопку вперед */
const onClickNext = () => {
  if (viewGridType.value === PickerGridTypeEnum.Dates) toNextMonth();
  else if (viewGridType.value === PickerGridTypeEnum.Years) toNextYearList();
};

/**При клике на кнопку назад */
const onClickPrev = () => {
  if (viewGridType.value === PickerGridTypeEnum.Dates) toPrevMonth();
  else if (viewGridType.value == PickerGridTypeEnum.Years) toPrevYearList();
};

/**Перейти к следующему месяцу */
const toNextMonth = () => {
  if (viewMonth.value === 11) {
    viewYear.value++;
    viewMonth.value = 0;
    return;
  }
  viewMonth.value++;
};
/**Перейти к предыдущему месяцу */
const toPrevMonth = () => {
  if (viewMonth.value === 0) {
    viewYear.value--;
    viewMonth.value = 11;
    return;
  }
  viewMonth.value--;
};

/**Перейти к следующему списку годов */
const toNextYearList = () => {
  firstYear.value += 12;
};

/**Перейти к предыдущему списку годов */
const toPrevYearList = () => {
  firstYear.value -= 12;
};

/**При открытии */
const onOpen = () => {
  viewYear.value = getViewYear();
  viewMonth.value = getViewMonth();
  firstYear.value = getFirstYear();
};
/**При закрытии */
const onClose = () => {
  viewGridType.value = getGridTypeByDatepickerType(props.type);
};

const onClickHeaderButton = () => {
  switch (viewGridType.value) {
    case PickerGridTypeEnum.Dates:
      viewGridType.value = PickerGridTypeEnum.Months;
      break;
    case PickerGridTypeEnum.Months:
      viewGridType.value = PickerGridTypeEnum.Years;
      firstYear.value = getFirstYear();
      break;
    case PickerGridTypeEnum.Hours:
    case PickerGridTypeEnum.Minutes:
      viewGridType.value = PickerGridTypeEnum.Dates;
  }
};

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue) {
      viewYear.value = getViewYear();
      viewMonth.value = getViewMonth();
    }
  },
);

onMounted(() => {
  uuid.value = uuidv4();
  document.addEventListener("closeOtherPickers", onCloseOtherPickers);
});
onUnmounted(() => {
  document.removeEventListener("closeOtherPickers", onCloseOtherPickers);
});

__expose({
  open,
  close,
  toggle,
  $datepickerInput,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(PickerContainer), {
    ref_key: "$pickerContainer",
    ref: $pickerContainer,
    type: __props.type,
    "grid-type": viewGridType.value,
    "is-disabled": __props.isDisabled,
    popover: popoverProps.value,
    onOpen: onOpen,
    onClose: onClose
  }, {
    default: _withCtx(({ open, close, toggle }) => [
      _renderSlot(_ctx.$slots, "default", {
        open: open,
        close: close,
        toggle: toggle
      }, () => [
        _createVNode(_unref(DatepickerInput), {
          ref_key: "$datepickerInput",
          ref: $datepickerInput,
          "model-value": __props.modelValue,
          type: __props.type,
          settings: inputProps.value,
          "is-disabled": __props.isDisabled,
          onClick: _withModifiers(closeOtherPickers, ["stop"]),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (emit('update:modelValue', $event))),
          onFocus: ($event: any) => (openFromFocus.value && toggle()),
          onEnter: _cache[1] || (_cache[1] = ($event: any) => (emit('enter', $event))),
          onBlur: _cache[2] || (_cache[2] = ($event: any) => (emit('blur', $event)))
        }, {
          hintMessage: _withCtx(() => [
            _renderSlot(_ctx.$slots, "hintMessage")
          ]),
          default: _withCtx(() => [
            (!openFromFocus.value)
              ? (_openBlock(), _createBlock(_unref(TirPmButton), {
                  key: 0,
                  class: "tir-pm-datepicker__input-button",
                  size: _unref(TirPmButtonSizeEnum).Small,
                  variant: _unref(TirPmButtonVariantEnum).LinkState,
                  onClick: toggle
                }, {
                  leftSlot: _withCtx(() => [
                    (__props.type === _unref(DatepickerTypeEnum).Time)
                      ? (_openBlock(), _createBlock(_unref(ClockIcon), { key: 0 }))
                      : (_openBlock(), _createBlock(_unref(CalendarIcon), {
                          key: 1,
                          class: _normalizeClass({ 'calendar-icon-disabled': __props.isDisabled })
                        }, null, 8, ["class"]))
                  ]),
                  _: 2
                }, 1032, ["size", "variant", "onClick"]))
              : _createCommentVNode("", true)
          ]),
          _: 2
        }, 1032, ["model-value", "type", "settings", "is-disabled", "onClick", "onFocus"])
      ])
    ]),
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (headerButtonText.value && __props.type !== _unref(DatepickerTypeEnum).Month)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (viewGridType.value === _unref(PickerGridTypeEnum).Years)
                ? (_openBlock(), _createBlock(_unref(TirPmButton), {
                    key: 0,
                    class: "tir-pm-datepicker__header-button",
                    variant: _unref(TirPmButtonVariantEnum).LinkState,
                    size: _unref(TirPmButtonSizeEnum).Small,
                    onClick: onClickHeaderButton
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("span", { innerHTML: headerButtonText.value }, null, 8, _hoisted_2)
                    ]),
                    _: 1
                  }, 8, ["variant", "size"]))
                : (_openBlock(), _createBlock(_unref(TirPmButton), {
                    key: 1,
                    class: "tir-pm-datepicker__header-button",
                    variant: _unref(TirPmButtonVariantEnum).LinkState,
                    size: _unref(TirPmButtonSizeEnum).Small,
                    onClick: onClickHeaderButton
                  }, {
                    rightSlot: _withCtx(() => [
                      _createVNode(_unref(ChevronDownIcon))
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("span", { innerHTML: headerButtonText.value }, null, 8, _hoisted_3)
                    ]),
                    _: 1
                  }, 8, ["variant", "size"]))
            ], 64))
          : _createCommentVNode("", true),
        (visibleClockButton.value || visibleNavigate.value || headerLabel.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (visibleClockButton.value)
                ? (_openBlock(), _createBlock(_unref(TirPmButton), {
                    key: 0,
                    class: "tir-pm-datepicker__time-button",
                    variant: _unref(TirPmButtonVariantEnum).LinkState,
                    size: _unref(TirPmButtonSizeEnum).Small,
                    onClick: onClickClock
                  }, {
                    leftSlot: _withCtx(() => [
                      _createVNode(_unref(ClockIcon))
                    ]),
                    _: 1
                  }, 8, ["variant", "size"]))
                : _createCommentVNode("", true),
              (visibleNavigate.value)
                ? (_openBlock(), _createBlock(_unref(NavigationConrols), {
                    key: 1,
                    onBack: onClickPrev,
                    onNext: onClickNext
                  }))
                : _createCommentVNode("", true),
              (headerLabel.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(headerLabel.value), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      (viewGridType.value === _unref(PickerGridTypeEnum).Dates)
        ? (_openBlock(), _createBlock(_unref(DatesGrid), {
            key: 0,
            modelValue: datesGridValue.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((datesGridValue).value = $event)),
            "is-week": __props.type === _unref(DatepickerTypeEnum).Week,
            "view-year": viewYear.value,
            "view-month": viewMonth.value
          }, {
            cell: _withCtx(({ cell }) => [
              _renderSlot(_ctx.$slots, "dayСell", { cell: cell })
            ]),
            "week-cell": _withCtx(({ weekNum }) => [
              _renderSlot(_ctx.$slots, "weekСell", { weekNum: weekNum })
            ]),
            _: 3
          }, 8, ["modelValue", "is-week", "view-year", "view-month"]))
        : (viewGridType.value === _unref(PickerGridTypeEnum).Months)
          ? (_openBlock(), _createBlock(_unref(MonthsGrid), {
              key: 1,
              modelValue: monthsGridValue.value,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((monthsGridValue).value = $event)),
              "view-year": viewYear.value,
              "in-monthpicker": __props.type === _unref(DatepickerTypeEnum).Month
            }, {
              cell: _withCtx(({ cell }) => [
                _renderSlot(_ctx.$slots, "monthСell", { cell: cell })
              ]),
              _: 3
            }, 8, ["modelValue", "view-year", "in-monthpicker"]))
          : (viewGridType.value === _unref(PickerGridTypeEnum).Years)
            ? (_openBlock(), _createBlock(_unref(YearsGrid), {
                key: 2,
                modelValue: yearsGridValue.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((yearsGridValue).value = $event)),
                "first-year": firstYear.value,
                "in-yearpicker": __props.type === _unref(DatepickerTypeEnum).Year
              }, {
                cell: _withCtx(({ cell }) => [
                  _renderSlot(_ctx.$slots, "yearСell", { cell: cell })
                ]),
                _: 3
              }, 8, ["modelValue", "first-year", "in-yearpicker"]))
            : (viewGridType.value === _unref(PickerGridTypeEnum).Hours)
              ? (_openBlock(), _createBlock(_unref(HoursGrid), {
                  key: 3,
                  modelValue: hoursGridValue.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((hoursGridValue).value = $event))
                }, {
                  cell: _withCtx(({ cell }) => [
                    _renderSlot(_ctx.$slots, "hourСell", { cell: cell })
                  ]),
                  _: 3
                }, 8, ["modelValue"]))
              : (viewGridType.value === _unref(PickerGridTypeEnum).Minutes)
                ? (_openBlock(), _createBlock(_unref(MinutesGrid), {
                    key: 4,
                    modelValue: minutesGridValue.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((minutesGridValue).value = $event)),
                    interval: __props.minutesInterval
                  }, {
                    cell: _withCtx(({ cell }) => [
                      _renderSlot(_ctx.$slots, "minuteСell", { cell: cell })
                    ]),
                    _: 3
                  }, 8, ["modelValue", "interval"]))
                : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["type", "grid-type", "is-disabled", "popover"]))
}
}

})